import { useEffect, useState } from "react";
import { Loader2, Linkedin, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

type LinkedInState = {
  linkedin_member_id: string | null;
  linkedin_verified_at: string | null;
  linkedin_raw: { name?: string; picture?: string; email?: string } | null;
};

const LinkedInCard = () => {
  const { user } = useAuth();
  const [state, setState] = useState<LinkedInState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("user_linkedin_profiles")
      .select("linkedin_member_id, linkedin_verified_at, linkedin_raw")
      .eq("user_id", user.id)
      .maybeSingle();
    setState((data as LinkedInState) ?? null);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const linkNow = async () => {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("link-linkedin", {
      body: { action: "link" },
    });
    setBusy(false);
    if (error || (data && (data as any).error)) {
      toast.error((data as any)?.error || error?.message || "Could not link LinkedIn");
      return;
    }
    toast.success("LinkedIn connected");
    await load();
  };

  const unlink = async () => {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("link-linkedin", {
      body: { action: "unlink" },
    });
    setBusy(false);
    if (error || (data && (data as any).error)) {
      toast.error((data as any)?.error || error?.message || "Could not unlink");
      return;
    }
    toast.success("LinkedIn disconnected");
    await load();
  };

  const linked = !!state?.linkedin_member_id;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Linkedin className="h-4 w-4 text-[#0a66c2]" /> LinkedIn
        </CardTitle>
        <CardDescription>
          Verify your professional identity and auto-fill your name and photo from LinkedIn.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : linked ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 rounded-md border border-border bg-muted/30 p-3">
              {state?.linkedin_raw?.picture && (
                <img
                  src={state.linkedin_raw.picture}
                  alt=""
                  className="h-10 w-10 rounded-full border border-border"
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 text-sm font-medium">
                  <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                  {state?.linkedin_raw?.name ?? "Connected"}
                </p>
                {state?.linkedin_verified_at && (
                  <p className="text-xs text-muted-foreground">
                    Verified {new Date(state.linkedin_verified_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={linkNow} disabled={busy}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Refresh from LinkedIn
              </Button>
              <Button variant="ghost" onClick={unlink} disabled={busy}>
                Disconnect
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
              Note: this v1 verifies via the emrkt workspace's LinkedIn connection. Per-user LinkedIn sign-in is on the roadmap.
            </p>
            <Button onClick={linkNow} disabled={busy} className="bg-[#0a66c2] text-white hover:bg-[#084d94]">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Linkedin className="mr-2 h-4 w-4" /> Connect LinkedIn
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default LinkedInCard;